import { useState } from 'react';
import { Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';

import notificationApi from '@/core/notifications/api/notification-api';
import { Contact, NotificationChannel, Subscription } from '@/core/notifications/model';
import ChannelPicker from '@/core/notifications/components/subscription-wizard/steps/channel-picker';
import InlineContactRow from '@/core/notifications/components/subscription-wizard/steps/inline-contact-row';
import { useOpenWidget } from '@/core/widget-navigation/export';

// The form itself, with no modal chrome. Exported separately so tests can
// exercise it directly — see quick-edit-name-popover.tsx for why.
// `QuickEditChannelsPopover` below is the thin Modal wrapper (a centered
// dialog, not an anchored popover — see quick-edit-name-popover.tsx for why).
//
// Reuses the exact same add-contact wiring as the wizard's delivery step
// (channel-picker.tsx's requiresVerification gating + InlineContactRow) —
// a channel that doesn't require verification can get a new contact added
// right here; one that does only gets a link to Contacts settings.
export function QuickEditChannelsPopoverContent({
  subscription,
  channels,
  contacts,
  onClose,
  onSaved,
}: {
  subscription: Subscription;
  channels: NotificationChannel[];
  contacts: Contact[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const openWidget = useOpenWidget();
  const [selectedChannels, setSelectedChannels] = useState<string[]>(() =>
    subscription.defaultChannels.includes('in-app')
      ? subscription.defaultChannels
      : ['in-app', ...subscription.defaultChannels],
  );
  const [overrides, setOverrides] = useState<Record<string, Record<string, unknown>>>(
    () => subscription.channelOverrides ?? {},
  );
  // Shadows the `contacts` prop so a contact added inline (below) shows up
  // immediately without waiting on the parent list to re-fetch and re-render.
  const [localContacts, setLocalContacts] = useState<Contact[]>(contacts);
  const [saving, setSaving] = useState(false);

  const toggleChannel = (channelKey: string) => {
    setSelectedChannels(prev =>
      prev.includes(channelKey)
        ? prev.filter(existing => existing !== channelKey)
        : [...prev, channelKey],
    );
  };

  const setOverride = (channelKey: string, config: Record<string, unknown>) => {
    setOverrides(prev => ({ ...prev, [channelKey]: config }));
  };

  const buildChannelOverrides = (): Record<string, Record<string, unknown>> => {
    const result: Record<string, Record<string, unknown>> = {};
    selectedChannels.forEach(channelKey => {
      const override = overrides[channelKey];
      if (override && Object.keys(override).length > 0) result[channelKey] = override;
    });
    return result;
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await notificationApi.updateSubscription(subscription.id, {
        defaultChannels: selectedChannels,
        channelOverrides: buildChannelOverrides(),
      });
      toast.success('Delivery channels updated');
      onSaved();
    } catch (e) {
      toast.error('Could not update delivery channels');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <ChannelPicker
        channels={channels}
        selectedChannels={selectedChannels}
        overrides={overrides}
        contacts={localContacts}
        onToggleChannel={toggleChannel}
        onOverrideChange={setOverride}
        renderAddContact={channelKey => {
          const channel = channels.find(c => c.key === channelKey);
          if (!channel) return null;
          return (
            <InlineContactRow
              channel={channel}
              onAdded={(key, refreshedContacts, addedContact) => {
                if (refreshedContacts) setLocalContacts(refreshedContacts);
                setOverride(key, addedContact.config);
                if (!selectedChannels.includes(key)) toggleChannel(key);
              }}
            />
          );
        }}
        onManageContacts={() => openWidget('settings', 'notifications/contacts')}
        onManagePushDevices={() => openWidget('settings', 'notifications/contacts')}
      />
      <div className="d-flex justify-content-end gap-2 mt-2">
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onClick={onClose}
          disabled={saving}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-sm btn-tertiary text-base-light"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </>
  );
}

// Quick edit #2 of 3 on a subscription row (see subscription-list.tsx): reuses
// the same ChannelPicker as the wizard's delivery step, scoped to this one
// subscription. PATCHes only `defaultChannels` + `channelOverrides`.
export default function QuickEditChannelsPopover({
  subscription,
  channels,
  contacts,
  onClose,
  onSaved,
}: {
  subscription: Subscription;
  channels: NotificationChannel[];
  contacts: Contact[];
  onClose: () => void;
  onSaved: () => void;
}) {
  return (
    <Modal show onHide={onClose} centered className="notifications-settings">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 14, fontWeight: 700 }}>Delivery channels</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        <QuickEditChannelsPopoverContent
          subscription={subscription}
          channels={channels}
          contacts={contacts}
          onClose={onClose}
          onSaved={onSaved}
        />
      </Modal.Body>
    </Modal>
  );
}
